import {createAsyncThunk, createSlice, current} from '@reduxjs/toolkit';
import {
    deleteOpenParkingAPI,
    deleteTrafficLogAPI,
    editOpenParkingAPI,
    finishTrafficLogParkingApi,
    getAllOpenParkingAPI,
    insertOrUpdateTrafficLogAPI,
    insertOtherTrafficLogAPI,
    insertTrafficLogAPI,
    searchOtherTrafficLogsApi,
    searchTrafficLogActionsAPI,
    searchTrafficLogsApi,
    searchTripWithTrafficLogApi,
    startTrafficLogParkingApi,
    updateOtherTrafficLogAPI,
    updateTrafficLogAPI
} from "../../api/trafficLog";
import {parseResponseError} from "../../utils/other";

export const fetchAllTrafficLogs =
    createAsyncThunk('fetchAllTrafficLogs', async (payload) => {
        const response = (await searchTripWithTrafficLogApi(payload)).data
        return response
            .sort((a, b) => (a.platformNumber ?? Infinity) - (b.platformNumber ?? Infinity))
            .map((row, index) => ({
                ...row,
                fakeId: index,
                isFirst: row.firstStationId === payload.stationId,
                isLast: row.lastStationId === payload.stationId,
            }))
    })

export const fetchAllOpenParking =
    createAsyncThunk('fetchAllOpenParking', async (payload) => {
        const {params, operatorId, plate} = payload;
        return (await getAllOpenParkingAPI(operatorId, plate, params)).data
    })

export const editOpenParkingThunk = createAsyncThunk(
    "editOpenParkingThunk",
    async (payload) => {
        return (await editOpenParkingAPI(payload)).data
    })

export const deleteOpenParkingThunk = createAsyncThunk(
    "deleteOpenParkingThunk",
    async (payload) => {
        return (await deleteOpenParkingAPI(payload)).data
    })

export const searchOtherTrafficLogs =
    createAsyncThunk('searchOtherTrafficLogs', async (payload) => {
        return (await searchOtherTrafficLogsApi(payload)).data
    })

export const searchTrafficLogs =
    createAsyncThunk('searchTrafficLogs', async (payload) => {
        return await searchTrafficLogsApi(payload)
    })

export const searchTrafficLogActions =
    createAsyncThunk('searchTrafficLogActions', async ({filter, params}) => {
        return (await searchTrafficLogActionsAPI(filter, params)).data
    })

export const insertTrafficLog =
    createAsyncThunk('insertTrafficLog', async (payload, {rejectWithValue}) => {
        try {
            return (await insertTrafficLogAPI(payload)).data
        } catch (e) {
            return rejectWithValue(parseResponseError(e))
        }
    })

export const insertOtherTrafficLog =
    createAsyncThunk('insertOtherTrafficLog', async (payload, {rejectWithValue}) => {
        try {
            return (await insertOtherTrafficLogAPI(payload)).data
        } catch (e) {
            return rejectWithValue(parseResponseError(e))
        }
    })

export const updateOtherTrafficLog =
    createAsyncThunk('updateOtherTrafficLog', async (payload, {rejectWithValue}) => {
        try {
            return (await updateOtherTrafficLogAPI(payload)).data
        } catch (e) {
            return rejectWithValue(parseResponseError(e))
        }
    })

export const insertOrUpdateTrafficLog =
    createAsyncThunk('insertOrUpdateTrafficLog', async (payload, {rejectWithValue}) => {
        try {
            return (await insertOrUpdateTrafficLogAPI(payload)).data
        } catch (e) {
            return rejectWithValue(parseResponseError(e))
        }
    })

export const updateTrafficLog =
    createAsyncThunk('updateTrafficLog', async (payload, {rejectWithValue}) => {
        try {
            return (await updateTrafficLogAPI(payload)).data
        } catch (e) {
            return rejectWithValue(parseResponseError(e))
        }
    })

export const finishTrafficLogParking =
    createAsyncThunk('finishTrafficLogParking', async (payload, {rejectWithValue}) => {
        try {
            return (await finishTrafficLogParkingApi(payload)).data
        } catch (e) {
            return rejectWithValue(parseResponseError(e))
        }
    })

export const startTrafficLogParking =
    createAsyncThunk('startTrafficLogParking', async (payload, {rejectWithValue}) => {
        try {
            return (await startTrafficLogParkingApi(payload)).data
        } catch (e) {
            return rejectWithValue(parseResponseError(e))
        }
    })

export const deleteTrafficLog =
    createAsyncThunk('deleteTrafficLog', async (payload, {rejectWithValue}) => {
        try {
            return (await deleteTrafficLogAPI(payload)).data
        } catch (e) {
            return rejectWithValue(parseResponseError(e))
        }
    })

const initialState = {
    trafficLogs: [],
    openParking: {
        content: [],
        totalElements: 0
    },
    trafficLogsReport: [],
    loading: false,
    updating: false,
    other: {
        content: [],
        totalElements: 0
    },
    loadingOther: false,
    loadingOpenParking: false,
    updatingOpenParking: false,
    actionLog: {
        content: [],
        totalElements: 0
    },
    loadingActionLog: false,
}

export const trafficLogSlice = createSlice({
    name: 'trafficLogs',
    initialState,
    reducers: {
        updateTrafficLogFromEvent: (state, {payload}) => {
            const arr = [...current(state.trafficLogs)];
            const index = arr.findIndex(p => p.tripId === payload.trip.id &&
                p.tripHasVehicleId === payload.expectedTripHasVehicleId);
            if (index !== -1) {
                const obj = {...arr[index]};
                setTrafficLogData(obj, payload);
                arr[index] = obj;
                state.trafficLogs = arr;
            }
        }
    },
    extraReducers: {
        [searchTrafficLogs.pending]: (state) => {
            state.loading = true;
        },
        [searchTrafficLogs.fulfilled]: (state, {payload}) => {
            state.trafficLogsReport = payload.data;
            state.loading = false;
        },
        [searchTrafficLogs.rejected]: (state) => {
            state.loading = false;
        },
        [fetchAllOpenParking.pending]: (state) => {
            state.loadingOpenParking = true;
        },
        [fetchAllOpenParking.fulfilled]: (state, {payload}) => {
            state.openParking = payload;
            state.loadingOpenParking = false;
        },
        [fetchAllOpenParking.rejected]: (state) => {
            state.loadingOpenParking = false;
        },
        [deleteOpenParkingThunk.pending]: (state) => {
            state.updatingOpenParking = true;
        },
        [deleteOpenParkingThunk.fulfilled]: (state, {payload}) => {
            const cState = current(state.openParking);
            state.openParking = {
                ...cState,
                content: cState.content.filter(s => s.id !== payload.id)
            }
            const arr = [...current(state.trafficLogs)];
            const index = arr.findIndex(p => p.trafficLogId === payload.id);
            if (index !== -1) {
                const obj = {...arr[index]};
                setTrafficLogData(obj, payload);
                arr[index] = obj;
                state.trafficLogs = arr;
            }
            state.updatingOpenParking = false;
        },
        [deleteOpenParkingThunk.rejected]: (state) => {
            state.updatingOpenParking = false;
        },
        [editOpenParkingThunk.pending]: (state) => {
            state.updatingOpenParking = true;
        },
        [editOpenParkingThunk.fulfilled]: (state, {payload}) => {
            const cState = current(state.openParking);
            const content = [...cState.content];
            const opIndex = cState.content?.findIndex(s => s.id === payload.id)
            if (opIndex !== -1) {
                content[opIndex] = payload;
            }
            state.openParking = {
                ...cState,
                content: content
            }
            const arr = [...current(state.trafficLogs)];
            const index = arr.findIndex(p => p.trafficLogId === payload.id);
            if (index !== -1) {
                const obj = {...arr[index]};
                setTrafficLogData(obj, payload);
                arr[index] = obj;
                state.trafficLogs = arr;
            }
            state.updatingOpenParking = false;
        },
        [editOpenParkingThunk.rejected]: (state) => {
            state.updatingOpenParking = false;
        },
        [fetchAllTrafficLogs.pending]: (state) => {
            state.loading = true;
        },
        [fetchAllTrafficLogs.fulfilled]: (state, {payload}) => {
            state.trafficLogs = payload;
            state.loading = false;
        },
        [fetchAllTrafficLogs.rejected]: (state) => {
            state.loading = false;
        },
        [insertTrafficLog.pending]: (state) => {
            state.loading = true;
        },
        [insertTrafficLog.fulfilled]: (state, {payload}) => {
            const arr = [...current(state.trafficLogs)];
            const index = arr.findIndex(p => p.tripId === payload.trip.id &&
                p.tripHasVehicleId === payload.expectedTripHasVehicleId);
            if (index !== -1) {
                const obj = {...arr[index]};
                setTrafficLogData(obj, payload);
                arr[index] = obj;
                state.trafficLogs = arr;
            }
            state.loading = false;
        },
        [insertTrafficLog.rejected]: (state) => {
            state.loading = false;
        },
        [insertOrUpdateTrafficLog.pending]: (state) => {
            state.loading = true;
        },
        [insertOrUpdateTrafficLog.fulfilled]: (state, {payload}) => {
            const arr = [...current(state.trafficLogs)];
            const index = arr.findIndex(p => p.tripId === payload.trip.id &&
                p.tripHasVehicleId === payload.expectedTripHasVehicleId);
            if (index !== -1) {
                const obj = {...arr[index]};
                setTrafficLogData(obj, payload);
                arr[index] = obj;
                state.trafficLogs = arr;
            }
            state.loading = false;
        },
        [insertOrUpdateTrafficLog.rejected]: (state) => {
            state.loading = false;
        },
        [updateTrafficLog.pending]: (state) => {
            state.loading = true;
        },
        [updateTrafficLog.fulfilled]: (state, {payload}) => {
            const arr = [...current(state.trafficLogs)];
            const index = arr.findIndex(p => p.tripId === payload.trip.id &&
                p.tripHasVehicleId === payload.expectedTripHasVehicleId);
            if (index !== -1) {
                const obj = {...arr[index]};
                setTrafficLogData(obj, payload);
                arr[index] = obj;
                state.trafficLogs = arr;
            }
            state.loading = false;
        },
        [updateTrafficLog.rejected]: (state) => {
            state.loading = false;
        },
        [finishTrafficLogParking.fulfilled]: (state, {payload}) => {
            const arr = [...current(state.trafficLogs)];
            const index = arr.findIndex(p => p.trafficLogId === payload.id);
            if (index !== -1) {
                const obj = {...arr[index]};
                setTrafficLogData(obj, payload);
                arr[index] = obj;
                state.trafficLogs = arr;
            }
            state.loading = false;
        },
        [startTrafficLogParking.fulfilled]: (state, {payload}) => {
            const arr = [...current(state.trafficLogs)];
            const index = arr.findIndex(p => p.tripId === payload.trip.id &&
                p.tripHasVehicleId === payload.expectedTripHasVehicleId);
            if (index !== -1) {
                const obj = {...arr[index]};
                setTrafficLogData(obj, payload);
                arr[index] = obj;
                state.trafficLogs = arr;
            }
            state.loading = false;
        },
        [searchTrafficLogActions.pending]: (state) => {
            state.loadingActionLog = true;
        },
        [searchTrafficLogActions.fulfilled]: (state, {payload}) => {
            state.actionLog = {
                content: payload.content || [],
                totalElements: payload.totalElements || 0
            };
            state.loadingActionLog = false;
        },
        [searchTrafficLogActions.rejected]: (state) => {
            state.loadingActionLog = false;
        },
        [searchOtherTrafficLogs.pending]: (state) => {
            state.loadingOther = true;
        },
        [searchOtherTrafficLogs.fulfilled]: (state, {payload}) => {
            state.other = payload;
            state.loadingOther = false;
        },
        [searchOtherTrafficLogs.rejected]: (state) => {
            state.loadingOther = false;
        },
        [deleteTrafficLog.pending]: (state) => {
            state.updating = true;
        },
        [deleteTrafficLog.fulfilled]: (state, {payload}) => {
            const arr = [...current(state.trafficLogs)];
            const index = arr.findIndex(p => p.trafficLogId === payload.id);
            if (index !== -1) {
                const obj = {...arr[index]};
                obj.trafficLogId = null;
                obj.tDepartureTime = null;
                obj.tArrivalTime = null;
                obj.departureVehicleId = null;
                obj.arrivalVehicleId = null;
                obj.tArrivalTime = null;
                obj.note = null;
                obj.arrVehicle = null;
                obj.arrPlateNumber = null;
                obj.arrivalVehicleId = null;
                obj.depVehicle = null;
                obj.depPlateNumber = null;
                obj.departureVehicleId = null;
                obj.realOperatorName = null;
                obj.realOperatorId = null;
                arr[index] = obj;
                state.trafficLogs = arr;
            }
            state.updating = false;
        },
        [deleteTrafficLog.rejected]: (state) => {
            state.updating = false;
        },
    }
});

const setTrafficLogData = (obj, response) => {
    obj.trafficLogId = response.id;
    obj.tDepartureTime = response.departureTime;
    obj.tArrivalTime = response.arrivalTime;
    obj.departureVehicleId = response.depVehicleId;
    obj.arrivalVehicleId = response.arrVehicleId;
    obj.tArrivalTime = response.arrivalTime;
    obj.note = response.note;
    if (response.arrVehicle) {
        obj.arrVehicle = response.arrVehicle;
        obj.arrPlateNumber = response.arrVehicle.plateNumber;
        obj.arrivalVehicleId = response.arrVehicle.id;
    } else if (response.arrPlateNumber) {
        // Tablica uneta slobodno (nema registrovano vozilo) - bez ovoga bi kolona
        // "Reg. broj" ostala prazna do sledećeg fetch-a.
        obj.arrPlateNumber = response.arrPlateNumber;
    }
    if (response.depVehicle) {
        obj.depVehicle = response.depVehicle;
        obj.depPlateNumber = response.depVehicle.plateNumber;
        obj.departureVehicleId = response.depVehicle.id;
    } else if (response.depPlateNumber) {
        obj.depPlateNumber = response.depPlateNumber;
    }
    if (response?.realOperator) {
        obj.realOperatorName = response?.realOperator?.name;
        obj.realOperatorId = response?.realOperator?.id;
    }
}

export const {
    updateTrafficLogFromEvent
} = trafficLogSlice.actions
export const trafficLogsReducer = trafficLogSlice.reducer
